/**
 * staleChunkRecovery — global Vite dynamic-import failure handler.
 *
 * Problem: after a deploy, cached tabs hold stale hashed chunk URLs
 * (e.g. `jszip.min-CNVVhbtO.js`) that 404 on the new CDN/server because
 * the bundle was renamed (e.g. `jszip.min-UXXINw8M.js`). Vite fires a
 * `vite:preloadError` event (plus a generic `error` / `unhandledrejection`
 * in some browsers) when the dynamic import fails.
 *
 * Strategy:
 *   1st failure in a session  → write a sessionStorage record + reload.
 *   Same-path failure within COOLDOWN_MS after a reload → toast (no loop).
 *   Different-path failure after cooldown → treat as a fresh deploy, reload.
 *
 * TTL: sessionStorage (tab-scoped). Does NOT persist across new tabs or
 * browser restart, which is the correct scope — each new tab bootstraps
 * fresh assets.
 *
 * SSR safety: the install function is a no-op when `window` is undefined.
 *
 * Surfaced 2026-05-20 — founder hit `Failed to fetch dynamically imported
 * module: jszip.min-CNVVhbtO.js` after PR #114 deployed mid-session.
 */
import { useToastStore } from "@/stores/useToastStore";

const SS_KEY = "certain.stale-chunk-reload";
const COOLDOWN_MS = 10_000;

/** Patterns that identify a stale dynamic-import failure message. */
const STALE_PATTERNS = [
  "Failed to fetch dynamically imported module",
  "error loading dynamically imported module",
] as const;

interface StaleRecord {
  path: string;
  ts: number;
}

function readRecord(): StaleRecord | null {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StaleRecord;
  } catch {
    return null;
  }
}

function writeRecord(r: StaleRecord): void {
  try {
    sessionStorage.setItem(SS_KEY, JSON.stringify(r));
  } catch {
    // sessionStorage may be unavailable (private mode quota, etc.). Fail
    // silently — worst case we reload one extra time.
  }
}

function surfaceToast(): void {
  useToastStore
    .getState()
    .addToast("App updated — please refresh to continue.", "warning");
}

/**
 * Core decision tree. Called on every suspected stale-chunk failure.
 *
 * @param path — the module URL that failed (best-effort; empty string ok)
 */
function handleStaleImport(path: string): void {
  const prior = readRecord();
  const now = Date.now();
  const samePath = prior !== null && prior.path === path;
  const recent = prior !== null && now - prior.ts < COOLDOWN_MS;

  if (samePath && recent) {
    // We reloaded for this exact path moments ago; another reload will loop.
    surfaceToast();
    return;
  }

  // Either first failure, a different path, or same path but cooldown elapsed.
  writeRecord({ path, ts: now });
  window.location.reload();
}

function isStaleMessage(msg: string): boolean {
  return STALE_PATTERNS.some((p) => msg.includes(p));
}

/**
 * Install the global stale-chunk recovery listeners.
 * Safe to call multiple times — de-duplicated via a module-level flag.
 * Must be called from `main.tsx` before `createRoot(...).render(...)`.
 *
 * Returns a teardown function that removes all registered listeners.
 * The teardown is intended for testing only — production code does not
 * need to call it.
 */
let installed = false;

export function installStaleChunkRecovery(): () => void {
  if (typeof window === "undefined") return () => undefined;
  if (installed) return () => undefined;
  installed = true;

  const onPreloadError = (event: Event): void => {
    const payload = (event as CustomEvent<{ path?: string }>).detail ?? {};
    handleStaleImport(payload.path ?? "");
  };

  const onError = (event: ErrorEvent): void => {
    const msg = event.message ?? "";
    if (isStaleMessage(msg)) {
      handleStaleImport(msg);
    }
  };

  const onUnhandledRejection = (event: Event): void => {
    const reason = (event as PromiseRejectionEvent).reason;
    const msg =
      typeof reason === "string"
        ? reason
        : reason instanceof Error
          ? reason.message
          : "";
    if (isStaleMessage(msg)) {
      handleStaleImport(msg);
    }
  };

  // 1. Vite canonical event (fired for every preload failure in Vite ≥4).
  window.addEventListener("vite:preloadError", onPreloadError);

  // 2. Generic window error — Chromium / some older Vite versions may
  //    surface stale-chunk failures here instead of `vite:preloadError`.
  window.addEventListener("error", onError);

  // 3. Unhandled promise rejection — dynamic import failures that aren't
  //    caught propagate as `unhandledrejection` in some environments.
  window.addEventListener("unhandledrejection", onUnhandledRejection);

  return () => {
    window.removeEventListener("vite:preloadError", onPreloadError);
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
    installed = false;
  };
}
